import { useTranslation } from 'react-i18next';
import { withStyles, BodyText, Box, Card, Preview } from 'vopay';
import { Title } from '#shared/components/UI';
import { BrandingValues } from '../BrandingForm/BrandingSettingsForm';
import { PreviewOption, usePreviewLabels } from './buildPreviewOptions.helper';
import styles from './PreviewScreen.module.scss';

const cn = withStyles(styles);

// Not the rendered size — the actual box is sized responsively via CSS (.viewer svg in
// PreviewScreen.module.scss, capped at 588px). These just give the SVG its native aspect
// ratio (588:408) so `height: auto` scales it proportionally as width shrinks.
const VIEWER_HEIGHT = 408;
const VIEWER_WIDTH = 588;

type PreviewScreenProps = {
  option: PreviewOption;
  brandingValues: BrandingValues;
  isEditing: boolean;
};

export default function PreviewScreen({ option, brandingValues, isEditing }: PreviewScreenProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Branding.Preview' });
  const productLabel = usePreviewLabels()[option.labelKey];

  return (
    <Card className={cn('card')}>
      <Box flx="col" gapSize="20px">
        <Box flx="col" gapSize="6px">
          <Title level="4">{t('cardTitle', { defaultValue: '{{product}} preview', product: productLabel })}</Title>

          <BodyText size="sm" className={cn('cardSubtitle')}>
            {isEditing
              ? t('cardSubtitleEditing', {
                  defaultValue: 'How your branding looks in {{product}} and updates as you edit.',
                  product: productLabel,
                })
              : t('cardSubtitleReadOnly', { defaultValue: 'Preview of the product applying changes above.' })}
          </BodyText>
        </Box>

        <div className={cn('divider')} />

        <div className={cn('viewer')}>
          <Preview
            preview={option.previewType}
            logo={brandingValues.Logo}
            primaryColor={brandingValues.PrimaryColor}
            secondaryColor={brandingValues.SecondaryColor}
            width={VIEWER_WIDTH}
            height={VIEWER_HEIGHT}
            // eLinx's tile covers both eLinx and Bank Verify
            slideShow={option.previewType === 'elinx'}
          />
        </div>
      </Box>
    </Card>
  );
}
